import { Box, Button, Flex, MediaBox, MediaFlex } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import React, { useRef } from "react"
import { Helmet } from "react-helmet-async"
import { connect } from "react-redux"
import styled from "styled-components"

import fetchProduct from "areas/product/hoc/fetch-product"
import withImages from "areas/product/hoc/with-images"
import ImageArea from "areas/product/image-area"
import { StyledImage } from "areas/product/jewlr/components"
import { trackSlide } from "areas/product/store/actions"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import { IS_JEWLR, retry } from "helpers/application"
import { isVideoView, isJ3dView } from "helpers/gallery-slide-view"
import { useNavigation } from "helpers/use-navigation"
import fetchWishlist from "hoc/fetch-wishlist"

import ImageAreaContent from "./image-area-content"
import MainBody from "./main-body"
import Breadcrumbs from "./wizard-header/breadcrumbs"
import OutOfStockBanner from "./wizard-header/out-of-stock-banner"

const GenericNotFound = loadable(() =>
  retry(() => import("app/generic-not-found"))
)
const ProductFooter = loadable(() => retry(() => import("./product-footer")), {
  ssr: false,
})
const CartPopup = loadable(() => retry(() => import("areas/cart/popup")))
const CartPreviewModal = loadable(() =>
  retry(() => import("areas/cart/preview"))
)
const HonestPricingModal = loadable(() =>
  retry(() => import("./modals/honest-pricing-modal"))
)
const ReturnPolicyModal = loadable(() =>
  retry(() => import("./modals/return-policy-modal"))
)
const PreviewImageModal = loadable(() =>
  retry(() => import("./modals/preview-image-modal"))
)

if (typeof window != "undefined") {
  try {
    if (window.location.hash != "") {
      var hash = window.location.hash.split("#")[1]
      if (hash.match("&gid=[0-9]*&pid=[0-9]*")) {
        window.location.hash = ""
      }
    }
  } catch (e) {
    console.log(e)
  }

  // EDGE ONLY - remove this line once Microsoft Edge decides to start supporting
  // HTMLCanvasElement.toBlob
  try {
    if (
      !Object.prototype.hasOwnProperty.call(
        HTMLCanvasElement.prototype,
        "toBlob"
      )
    ) {
      require("blueimp-canvas-to-blob")
    }
  } catch (e) {
    // Continue if this polyfill fails to load
    console.log("Couldn't load toBlob polyfill", e)
  }
}

const ThumbnailBtn = styled(Button).attrs({
  variant: "text",
})`
  border: 3px solid
    ${(props) => (props.selected ? props.theme.colors.grey[10] : "transparent")};
  display: block;
  position: relative;
  :hover {
    border-color: ${(props) => props.theme.colors.grey[5]};
  }
  :focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
  }
`

const Container = styled(Box).attrs((props) => ({
  contain: true,
  mb: { tablet: "10px" },
  pt: { tablet: 4 },
  ...props,
}))`
  ${(props) => props.theme.mediaQueries.tablet`
    margin-left: auto;
    margin-right: auto;
    max-width: 835px;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    max-width: 100%;
  `}
  ${(props) => props.theme.mediaQueries.widescreen`
    margin-left: 64px;
    margin-right: 64px;
  `}
  @media (min-width: 1568px) {
    max-width: 1440px;
    margin-left: auto;
    margin-right: auto;
  }
`
const Thumbnail = ({
  index,
  onClick,
  productTitle,
  selected,
  src,
  srcSet,
  tabIndex,
  view,
}) => {
  const keyboardProps = useNavigation()

  return (
    <ThumbnailBtn
      aria-controls={`image-slider-tabpanel-${index + 1}-main`}
      aria-label={`slide-${index + 1}`}
      aria-selected={selected}
      onClick={onClick}
      role="tab"
      selected={selected}
      tabIndex={tabIndex}
      {...keyboardProps}
    >
      <StyledImage
        alt={productTitle}
        className={
          view === "tangiblee_view"
            ? sl.config.env == "staging"
              ? "js-tangiblee-cta-v2"
              : "js-tangiblee-cta"
            : ""
        }
        data-cy={`product-main-slider-thumbnail-img-${index}`}
        height="48px"
        lazyload={index > 0}
        mb="2px"
        ratio="48 / 48"
        sizes="48px"
        src={src}
        srcSet={srcSet}
        width="48px"
      />
    </ThumbnailBtn>
  )
}

Thumbnail.propTypes = {
  index: PropTypes.number,
  onClick: PropTypes.func,
  productTitle: PropTypes.string,
  selected: PropTypes.bool,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  tabIndex: PropTypes.string,
  view: PropTypes.string,
}

/**
 * Product index
 * @param {object} data - default data from _react.html
 */
const InternalProduct = ({
  currentSlideIndex,
  j3dSlideIndex,
  outOfStock,
  productNotFound,
  productTitle,
  slidesArray,
  staticContext,
  styleCode,
  trackSlide,
  wizardLoading,
}) => {
  const wizardBodyRef = useRef(null)
  const outOfStockRef = useRef()

  const ssrNotFound =
    staticContext && styleCode === undefined && outOfStock === undefined

  if (productNotFound || ssrNotFound) {
    return <GenericNotFound staticContext={staticContext} />
  }

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        <meta content="240" name="og_image_width" property="og:image:width" />
        <meta content="240" name="og_image_height" property="og:image:height" />
        {slidesArray?.length > 0 && slidesArray[0].src && (
          <meta
            content={slidesArray[0].src}
            name="og_image"
            property="og:image"
          />
        )}
        {slidesArray?.length > 0 && slidesArray[0].srcset && (
          <link
            rel="preload"
            fetchPriority="high"
            as="image"
            imageSrcSet={slidesArray[0].srcset}
            imageSizes={slidesArray[0].sizes}
          />
        )}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <Container>
        {outOfStock && (
          <MediaBox lessThan="tablet" mb={3} px="20px">
            <OutOfStockBanner outOfStockRef={outOfStockRef} />
          </MediaBox>
        )}
        <Flex
          alignItems={{ tablet: "flex-start" }}
          flexDirection={{ _: "column", tablet: "row" }}
        >
          <Box
            flex="1"
            mb={{ tablet: 2 }}
            pr={{ tablet: 5 }}
            width={{ tablet: "50%" }}
          >
            <ImageArea
              endOffset={80}
              topOffset={30}
              wizardBodyRef={wizardLoading ? {} : wizardBodyRef}
            >
              <Flex gap="44px">
                <MediaFlex
                  aria-label="Product Images"
                  flexDirection="column"
                  gap="12px"
                  greaterThanOrEqual="desktop"
                  minWidth="52px"
                  role="tablist"
                >
                  <FocusScope>
                    {slidesArray?.length > 0 &&
                      slidesArray
                        .filter(({ view }) => !isJ3dView(view))
                        .map((slide, index) => (
                          <Thumbnail
                            index={index}
                            key={`mainthumb-${slide.view}`}
                            onClick={() => trackSlide(index)}
                            productTitle={productTitle}
                            selected={currentSlideIndex === index}
                            src={
                              isVideoView(slide.view)
                                ? require("images/product/vr/videoicon.png")
                                : slide.src
                            }
                            {...(!isVideoView(slide.view) && {
                              srcSet: slide.srcset,
                            })}
                            tabIndex={
                              currentSlideIndex === index ||
                              (currentSlideIndex === j3dSlideIndex &&
                                index === 0)
                                ? "0"
                                : "-1"
                            }
                            view={slide.view}
                          />
                        ))}
                  </FocusScope>
                </MediaFlex>
                <ImageAreaContent />
              </Flex>
            </ImageArea>
          </Box>
          <Box
            px={2}
            ref={wizardBodyRef}
            width={{ _: "100%", desktop: "480px", tablet: "50%" }}
          >
            <Breadcrumbs />
            <MainBody outOfStockRef={outOfStockRef} />
          </Box>
        </Flex>
      </Container>
      <ProductFooter />
      <SimpleDeferAfterLoadWrapper>
        <CartPopup />
        <CartPreviewModal />
        <PreviewImageModal />
        {IS_JEWLR() && <HonestPricingModal />}
        {IS_JEWLR() && <ReturnPolicyModal />}
      </SimpleDeferAfterLoadWrapper>
    </>
  )
}

InternalProduct.propTypes = {
  currentSlideIndex: PropTypes.number,
  j3dSlideIndex: PropTypes.number,
  outOfStock: PropTypes.bool,
  productNotFound: PropTypes.bool,
  productTitle: PropTypes.string,
  slidesArray: PropTypes.array,
  staticContext: PropTypes.object,
  styleCode: PropTypes.string,
  trackSlide: PropTypes.func,
  wizardLoading: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    outOfStock: state.wizard.header?.product_out_of_stock,
    productNotFound: state.wizardUI.productNotFound,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    trackSlide: (index) => {
      dispatch(trackSlide(index))
    },
  }
}

const Product = fetchWishlist(
  connect(mapStateToProps, mapDispatchToProps)(InternalProduct)
)

export default fetchProduct(withImages(Product))
